import { useEffect, useState } from "react";
import { useSeo } from "../lib/useSeo";
import { CalendarDays, MapPin } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { PageHeader } from "../components/PageHeader";
import { Section, Overline } from "../components/Primitives";
import { IMAGES } from "../data/content";
import { getEvents, getGallery } from "../lib/api";
import { resolveSrc } from "../components/ImageUpload";
import { ZoomImg } from "../components/ZoomImg";
import { useContent } from "../context/ContentContext";

const EventCard = ({ ev }) => (
  <FadeIn className="bg-amf-cream border border-amf-gold/30 p-8 md:p-10" data-testid="event-card">
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
      <span className="amf-overline text-amf-burgundy inline-flex items-center gap-2">
        <CalendarDays size={15} /> {ev.date}
      </span>
      <span className="amf-overline text-amf-stone inline-flex items-center gap-2">
        <MapPin size={15} /> {ev.location}
      </span>
    </div>
    <h3 className="font-playfair text-2xl text-amf-navy leading-snug">{ev.title}</h3>
    <p className="font-lora text-amf-charcoal/80 mt-4 leading-relaxed">{ev.description}</p>
  </FadeIn>
);

export default function Events() {
  useSeo("Events", "Upcoming and past initiatives and events from Asana Memorial Foundation.");
  const { t } = useContent();
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    getEvents().then(setEvents).catch(() => setEvents([]));
    getGallery().then(setGallery).catch(() => setGallery([]));
  }, []);

  const upcoming = events.filter((e) => e.category === "upcoming");
  const past = events.filter((e) => e.category === "past");

  return (
    <div data-testid="events-page">
      <PageHeader
        eyebrow={t("events.header.eyebrow")}
        title={t("events.header.title")}
        intro={t("events.header.intro")}
        image={IMAGES.studentsOutside}
      />

      <Section className="bg-amf-cream">
        <FadeIn className="mb-10">
          <Overline>Upcoming Events</Overline>
        </FadeIn>
        <div className="grid grid-cols-1 gap-8">
          {upcoming.length ? (
            upcoming.map((e) => <EventCard key={e.id} ev={e} />)
          ) : (
            <p className="font-lora text-amf-stone">New events forthcoming.</p>
          )}
        </div>
      </Section>

      <Section className="bg-amf-navy">
        <FadeIn className="mb-10">
          <Overline light>Past Events</Overline>
        </FadeIn>
        <div className="grid grid-cols-1 gap-px bg-amf-gold/25 border border-amf-gold/25">
          {past.map((e) => (
            <FadeIn key={e.id} className="bg-amf-navy p-8 md:p-10" data-testid="event-card">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
                <span className="amf-overline text-amf-gold inline-flex items-center gap-2">
                  <CalendarDays size={15} /> {e.date}
                </span>
                <span className="amf-overline text-amf-cream/60 inline-flex items-center gap-2">
                  <MapPin size={15} /> {e.location}
                </span>
              </div>
              <h3 className="font-playfair text-2xl text-amf-cream leading-snug">{e.title}</h3>
              <p className="font-lora text-amf-cream/75 mt-4 leading-relaxed">{e.description}</p>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* GALLERY */}
      <Section className="bg-amf-cream">
        <FadeIn className="mb-10">
          <Overline>Event Gallery</Overline>
          <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3">
            Scenes from our work
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((g, i) => (
            <FadeIn key={g.id || i} delay={(i % 3) * 0.1} data-testid="gallery-item">
              <ZoomImg
                src={resolveSrc(g.src)}
                alt={g.caption}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover amf-img"
              />
              <p className="amf-overline text-amf-stone mt-3">{g.caption}</p>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
}
